import type { AutopopulateOperationFragment, AutopopulateOperationsByEventIdQuery } from '../graphql/autoPopulation.graphql.generated';
import type { Maybe } from '@graphql/generated-base';
import { useAutopopulateOperationsByEventIdQuery } from '../graphql/autoPopulation.graphql.generated';
import { useAutoPopulationPolling as useBaseAutoPopulationPolling } from '@shared/utils/autoPopulation/useAutoPopulationPolling';
import type { UseAutoPopulationPollingReturn } from '@shared/utils/autoPopulation/useAutoPopulation.types';
import { DEFAULT_POLL_SCHEDULE_MS } from '@shared/utils/autoPopulation/useAutoPopulationPolling';
import type { AutoPopulationPollingResult, AccommodationsContext } from '../types';

interface UseAutoPopulationPollingOptions
  extends Readonly<{
    eventId?: Maybe<string>;
    shouldSkipAutoPopulationCheck: boolean;
    context: AccommodationsContext;
    skipPolling?: boolean;
  }> {}

/**
 * Configuration for extracting and mapping accommodation operations.
 */
const pollingConfig = {
  extractOperations: (data: AutopopulateOperationsByEventIdQuery | undefined) => {
    return data?.autopopulateOperationsByEventIdV20251203?.operations;
  },

  mapOperationResults: (operation: AutopopulateOperationFragment): ReadonlyArray<AutoPopulationPollingResult> => {
    return (operation.result?.items ?? []).map(place => ({
      algoTag: operation.result?.algoTag,
      accommodationPlaceId: place.accommodationPlaceId,
      whyThisHotelIsAGreatPick: place.whyThisHotelIsAGreatPick ?? '',
      proposedNoteFromHostToGuest: place.proposedNoteFromHostToGuest ?? '',
    }));
  },

  getOperationStatus: (operation: AutopopulateOperationFragment): string => {
    return operation.status;
  },
} as const;

/**
 * Hook to poll auto-populate accommodation operations status.
 * Wraps the base polling hook with accommodations-specific configuration.
 *
 * IMPORTANT: The query hook is called unconditionally at the top level to follow React's Rules of Hooks.
 * This ensures stable hook call order across renders.
 */
export const useAutoPopulationPolling = (options: UseAutoPopulationPollingOptions): UseAutoPopulationPollingReturn<AutoPopulationPollingResult> => {
  const { eventId, shouldSkipAutoPopulationCheck, context, skipPolling } = options;

  // Calculate skip condition for the query
  const shouldSkip = !eventId || shouldSkipAutoPopulationCheck || skipPolling;

  // Ensure fresh job status on mount; cache-and-network prevents stale COMPLETED data after cross-page navigation
  const queryResult = useAutopopulateOperationsByEventIdQuery({
    variables: { payload: { eventId: eventId! } },
    skip: shouldSkip,
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  return useBaseAutoPopulationPolling<AutopopulateOperationsByEventIdQuery, AutopopulateOperationFragment, AutoPopulationPollingResult, AccommodationsContext>({
    eventId,
    shouldSkipAutoPopulationCheck,
    context,
    queryResult,
    config: pollingConfig,
    pollScheduleMs: DEFAULT_POLL_SCHEDULE_MS,
  });
};
